export type MessageType = 'TEXT' | 'ATTACHMENT' | 'TEXT_ATTACHMENT';
export type MessageStatus = 'SENT' | 'DELIVERED' | 'READ';
export type UsuarioType = 'CLIENT' | 'ADMIN' | 'SYSTEM';
export type ChatSocketRole = 'ADMIN' | 'CLIENT';
export type AttachmentKind = 'IMAGE' | 'PDF' | 'DOCX' | 'OTHER';
export type AttachmentProcessingStatus = 'READY' | 'PROCESSING' | 'FAILED';

export type ChatMetadata = Record<string, unknown>;

export interface ChatAttachmentCreate {
  storage_path: string;
  thumbnail_path?: string | null;
  file_name: string;
  file_size: number;
  mime_type?: string | null;
  metadata_json?: ChatMetadata | null;
}

export interface ChatAttachment extends ChatAttachmentCreate {
  id_attachment: number;
  thumbnail_sas_url?: string | null;
  kind: AttachmentKind;
  preview_path?: string | null;
  preview_sas_url?: string | null;
  preview_mime_type?: string | null;
  preview_width?: number | null;
  preview_height?: number | null;
  original_width?: number | null;
  original_height?: number | null;
  page_count?: number | null;
  processing_status?: AttachmentProcessingStatus;
  created_at: string;
}

export interface ChatMessage {
  id_message: number;
  id_conversation: number;
  id_usuario: string;
  nombre_usuario?: string | null;
  usuario_type: UsuarioType;
  message_type: MessageType;
  body_text?: string | null;
  created_at: string;
  updated_at?: string | null;
  status: MessageStatus;
  id_replied_message?: number | null;
  metadata_json?: ChatMetadata | null;
  attachments: ChatAttachment[];
}

export interface AdminChatMessage extends ChatMessage {
  read_by_user: boolean;
}

export interface AdminChatMessagesResponse {
  items: AdminChatMessage[];
  has_more: boolean;
  next_cursor_id?: number | null;
}

export interface AdminChatMessageSearchResponse extends AdminChatMessagesResponse {
  found: boolean;
  matched_message_id?: number | null;
  matching_message_ids: number[];
  truncated_to_window: boolean;
  total_context_messages: number;
}

export interface ChatSendMessageRequest {
  body_text?: string | null;
  message_type: MessageType;
  attachments: ChatAttachmentCreate[];
  id_replied_message?: number | null;
  metadata_json?: ChatMetadata | null;
}

export type AdminSendMessageRequest = ChatSendMessageRequest;

export interface AdminEditMessageRequest {
  body_text: string;
}

export interface AdminConversationListItem {
  id_conversation: number;
  id_usuario: string;
  last_message_id?: number | null;
  last_message_at?: string | null;
  last_message_text?: string | null;
  last_message_usuario_type?: UsuarioType | null;
  last_message_type?: MessageType | null;
  unread_count_admin: number;
  admin_marked_unread: boolean;
}

export interface AdminConversationListResponse {
  items: AdminConversationListItem[];
}

export interface AdminUnreadCountResponse {
  total_unread: number;
}

export interface AdminUnreadCountByUserItem {
  id_usuario: string;
  unread_count_admin: number;
}

export interface AdminUnreadCountByUserResponse {
  items: AdminUnreadCountByUserItem[];
}

export interface AdminMarkReadResponse {
  id_usuario: string;
  marked_read_count: number;
}

export interface AdminMarkUnreadResponse {
  id_usuario: string;
  admin_marked_unread: boolean;
  conversation: AdminConversationListItem;
}

export interface AttachmentDownloadUrlResponse {
  id_attachment: number;
  download_sas_url: string;
}

export type ChatSocketEventType =
  | 'connection.accepted'
  | 'connection.error'
  | 'ping'
  | 'pong'
  | 'message.created'
  | 'message.edited'
  | 'messages.read'
  | 'attachment.preview.updated'
  | 'client.presence.updated'
  | 'clients.presence.snapshot'
  | 'unread_count.updated'
  | 'admin_unread_count.updated'
  | 'admin_user_unread_count.updated'
  | 'conversation.updated'
  | 'admin_status.invalidate';

export interface ChatSocketEvent<TPayload = unknown> {
  type: ChatSocketEventType;
  payload: TPayload;
  occurred_at: string;
}

export interface ChatConnectionAcceptedPayload {
  connection_id: string;
  id_usuario: string;
  role: ChatSocketRole;
}

export interface ChatConnectionErrorPayload {
  detail: string;
}

export interface ChatMessageEventPayload<TMessage extends ChatMessage = ChatMessage> {
  id_usuario: string;
  message: TMessage;
}

export interface ChatAttachmentPreviewUpdatedPayload {
  id_usuario: string;
  id_message: number;
  attachment: ChatAttachment;
}

export interface ChatMessagesReadPayload {
  id_usuario: string;
  message_ids: number[];
  read_by_user: boolean;
}

export interface ChatClientPresencePayload {
  id_usuario: string;
  online: boolean;
  connection_count: number;
}

export type ChatClientPresenceUpdatedPayload = ChatClientPresencePayload;

export interface ChatClientsPresenceSnapshotPayload {
  clients: ChatClientPresencePayload[];
}

export interface ChatConversationUpdatedPayload {
  conversation: AdminConversationListItem;
}

export interface ChatUnreadCountUpdatedPayload {
  unread_count_client: number;
}

export interface ChatAdminUnreadCountUpdatedPayload {
  total_unread: number;
}

export interface ChatAdminUserUnreadCountUpdatedPayload {
  id_usuario: string;
  unread_count_admin: number;
  admin_marked_unread?: boolean;
}

export interface ChatPingPayload extends ChatMetadata {
  client_ts: number;
}

export function buildMessageType(text: string | null | undefined, attachmentsCount: number): MessageType {
  const hasText = !!text?.trim();
  const hasAttachments = attachmentsCount > 0;

  if (hasText && hasAttachments) return 'TEXT_ATTACHMENT';
  if (hasAttachments) return 'ATTACHMENT';
  return 'TEXT';
}

export function hasValidChatContent(text: string | null | undefined, attachmentsCount: number): boolean {
  return !!text?.trim() || attachmentsCount > 0;
}
